import { useEffect, useRef } from 'react';
import { getAudioContext } from '@/lib/audio/audio-context';
import {
  DRONE_KEYS,
  type DroneKey,
  type DroneInterval,
  type DroneOctave,
} from '@/lib/practice/drone-keys';
import { intervalFrequencies } from './drone-frequencies';

const FADE_DURATION_SECONDS = 0.2;

// Highest master-gain value the slider can reach. `volume` (0–1 linear from the
// design slider) is squared into a perceptual curve before scaling by this.
export const MASTER_CEILING = 0.4;

// A touch of detune across the stacked oscillators thickens the drone.
const DETUNE_CENTS = 4;

export type UseDroneParams = {
  root: DroneKey | null;
  interval: DroneInterval;
  octave: DroneOctave;
  volume: number;
  on: boolean;
};

type DroneNodes = {
  oscillators: OscillatorNode[];
  master: GainNode;
};

function gainForVolume(volume: number): number {
  return volume ** 2 * MASTER_CEILING;
}

function baseHzForKey(root: DroneKey): number {
  return DRONE_KEYS.find((k) => k.note === root)?.baseHz ?? DRONE_KEYS[0].baseHz;
}

function startDrone(
  ctx: AudioContext,
  root: DroneKey,
  interval: DroneInterval,
  octave: DroneOctave,
  volume: number,
): DroneNodes {
  const frequencies = intervalFrequencies(baseHzForKey(root), interval, octave);

  const master = ctx.createGain();
  master.gain.setValueAtTime(0, ctx.currentTime);
  master.gain.linearRampToValueAtTime(
    gainForVolume(volume),
    ctx.currentTime + FADE_DURATION_SECONDS,
  );
  master.connect(ctx.destination);

  const oscillators = frequencies.map((frequency, index) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = frequency;
    osc.detune.value = index * DETUNE_CENTS;
    osc.connect(master);
    osc.start();
    return osc;
  });

  return { oscillators, master };
}

function stopDrone(ctx: AudioContext, nodes: DroneNodes): void {
  const stopTime = ctx.currentTime + FADE_DURATION_SECONDS;
  nodes.master.gain.setValueAtTime(nodes.master.gain.value, ctx.currentTime);
  nodes.master.gain.linearRampToValueAtTime(0, stopTime);
  for (const osc of nodes.oscillators) {
    osc.stop(stopTime);
  }
}

export function useDrone({ root, interval, octave, volume, on }: UseDroneParams): void {
  const nodesRef = useRef<DroneNodes | null>(null);

  useEffect(() => {
    const ctx = getAudioContext();

    if (nodesRef.current !== null) {
      stopDrone(ctx, nodesRef.current);
      nodesRef.current = null;
    }

    const shouldPlay = on && root !== null;
    if (!shouldPlay) return;

    nodesRef.current = startDrone(ctx, root, interval, octave, volume);

    return () => {
      if (nodesRef.current !== null) {
        stopDrone(ctx, nodesRef.current);
        nodesRef.current = null;
      }
    };
  }, [root, interval, octave, volume, on]);
}
